import { create } from 'zustand';
import { isArray, mergeWith } from 'lodash';
import { getGenericFeedbackTexts, getFeedbackTexts } from '../feedback-texts';
import {
  INFO,
  CRITICAL,
  WARNING,
  GREATER_THAN,
  LESS_THAN,
} from '@app/routes/admin/workspace/ai-review/constants';

const BREACH_SEVERITY = {
  sales: {
    disputeResolution: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    governingLaw: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    confidentiality: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: WARNING,
    },
    paymentTerm: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: WARNING,
    },
    exclusionOfConsequentialLoss: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    forceMajeure: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: WARNING,
    },
    indemnities: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    limitationOfLiability: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    liquidatedDamages: {
      breachSeverityWhenFound: WARNING,
      breachSeverityWhenMissing: INFO,
    },
  },
  procurement: {
    disputeResolution: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    governingLaw: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    confidentiality: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: WARNING,
    },
    paymentTerm: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: WARNING,
    },
    exclusionOfConsequentialLoss: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: WARNING,
    },
    forceMajeure: {
      breachSeverityWhenFound: WARNING,
      breachSeverityWhenMissing: INFO,
    },
    indemnities: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
    limitationOfLiability: {
      breachSeverityWhenFound: WARNING,
      breachSeverityWhenMissing: INFO,
    },
    liquidatedDamages: {
      breachSeverityWhenFound: INFO,
      breachSeverityWhenMissing: CRITICAL,
    },
  },
};

const getDefaultPlaybookValues = (
  workspaceType: 'sales' | 'procurement',
  country: string,
  t: any,
) => ({
  disputeResolution: {
    enabled: true,
    value: [
      'LABEL_ARBITRATION',
    ],
    feedbackTextExample: getGenericFeedbackTexts(t, 'disputeResolution').feedbackTextExample,
    feedbackTextWhatToDo: getGenericFeedbackTexts(t, 'disputeResolution').feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.disputeResolution.breachSeverityWhenFound,
    breachSeverityWhenMissing: BREACH_SEVERITY.sales.disputeResolution.breachSeverityWhenMissing,
  },
  governingLaw: {
    enabled: true,
    value: country
      ? [
          country,
        ]
      : [],
    feedbackTextExample: getGenericFeedbackTexts(t, 'governingLaw').feedbackTextExample,
    feedbackTextWhatToDo: getGenericFeedbackTexts(t, 'governingLaw').feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.governingLaw.breachSeverityWhenFound,
    breachSeverityWhenMissing: BREACH_SEVERITY.sales.governingLaw.breachSeverityWhenMissing,
  },
  confidentiality: {
    enabled: true,
    feedbackTextExample: getGenericFeedbackTexts(t, 'confidentiality').feedbackTextExample,
    feedbackTextWhatToDo: getGenericFeedbackTexts(t, 'confidentiality').feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.confidentiality.breachSeverityWhenFound,
    breachSeverityWhenMissing: BREACH_SEVERITY.sales.confidentiality.breachSeverityWhenMissing,
  },
  paymentTerm: {
    enabled: true,
    value: 'P30D',
    valueOperator: workspaceType === 'procurement' ? GREATER_THAN : LESS_THAN,
    feedbackTextExample: getFeedbackTexts(t, 'paymentTerm', workspaceType).feedbackTextExample,
    feedbackTextWhatToDo: getFeedbackTexts(t, 'paymentTerm', workspaceType).feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.paymentTerm.breachSeverityWhenFound,
    breachSeverityWhenMissing: BREACH_SEVERITY.sales.paymentTerm.breachSeverityWhenMissing,
  },
  exclusionOfConsequentialLoss: {
    enabled: true,
    feedbackTextExample: getFeedbackTexts(t, 'exclusionOfConsequentialLoss', workspaceType)
      .feedbackTextExample,
    feedbackTextWhatToDo: getFeedbackTexts(t, 'exclusionOfConsequentialLoss', workspaceType)
      .feedbackTextWhatToDo,
    breachSeverityWhenFound:
      BREACH_SEVERITY.sales.exclusionOfConsequentialLoss.breachSeverityWhenFound,
    breachSeverityWhenMissing:
      BREACH_SEVERITY.sales.exclusionOfConsequentialLoss.breachSeverityWhenMissing,
  },
  forceMajeure: {
    enabled: true,
    feedbackTextExample: getFeedbackTexts(t, 'forceMajeure', workspaceType).feedbackTextExample,
    feedbackTextWhatToDo: getFeedbackTexts(t, 'forceMajeure', workspaceType).feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.forceMajeure.breachSeverityWhenFound,
    breachSeverityWhenMissing: BREACH_SEVERITY.sales.forceMajeure.breachSeverityWhenMissing,
  },
  indemnities: {
    enabled: true,
    feedbackTextExample: getFeedbackTexts(t, 'indemnities', workspaceType).feedbackTextExample,
    feedbackTextWhatToDo: getFeedbackTexts(t, 'indemnities', workspaceType).feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.indemnities.breachSeverityWhenFound,
    breachSeverityWhenMissing: BREACH_SEVERITY.sales.indemnities.breachSeverityWhenMissing,
  },
  limitationOfLiability: {
    enabled: true,
    feedbackTextExample: getFeedbackTexts(t, 'limitationOfLiability', workspaceType)
      .feedbackTextExample,
    feedbackTextWhatToDo: getFeedbackTexts(t, 'limitationOfLiability', workspaceType)
      .feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.limitationOfLiability.breachSeverityWhenFound,
    breachSeverityWhenMissing:
      BREACH_SEVERITY.sales.limitationOfLiability.breachSeverityWhenMissing,
  },
  liquidatedDamages: {
    enabled: true,
    feedbackTextExample: getFeedbackTexts(t, 'liquidatedDamages', workspaceType)
      .feedbackTextExample,
    feedbackTextWhatToDo: getFeedbackTexts(t, 'liquidatedDamages', workspaceType)
      .feedbackTextWhatToDo,
    breachSeverityWhenFound: BREACH_SEVERITY.sales.liquidatedDamages.breachSeverityWhenFound,
    breachSeverityWhenMissing: BREACH_SEVERITY.sales.liquidatedDamages.breachSeverityWhenMissing,
  },
});

type BasePlaybookValue<T = undefined> = {
  enabled: boolean;
  value?: T; // Optional, used for paymentTerm, disputeResolution, governingLaw
  valueOperator?: string; // Optional, used for paymentTerm
  breachSeverityWhenFound: string;
  breachSeverityWhenMissing: string;
  feedbackTextWhatToDo: string;
  feedbackTextExample: string;
};

type PlaybookValues = {
  paymentTerm: BasePlaybookValue<string>;
  disputeResolution: BasePlaybookValue<string[]>;
  governingLaw: BasePlaybookValue<string[]>;
  confidentiality: BasePlaybookValue;
  exclusionOfConsequentialLoss: BasePlaybookValue;
  forceMajeure: BasePlaybookValue;
  indemnities: BasePlaybookValue;
  limitationOfLiability: BasePlaybookValue;
  liquidatedDamages: BasePlaybookValue;
};

type PlaybookResponse = {
  enabled: boolean;
  playbookValues: null | PlaybookValues;
};

interface PlaybookStore {
  playbookValues: PlaybookValues | null;
  playbookEnabled: boolean;
  initializeDefaultValues: (
    workspaceType: string,
    country: string,
    t: (translations: Record<string, string>) => string,
  ) => void;
  initializeResponseData: (data: PlaybookResponse, country: string) => void;
  resetPlaybookValues: () => void;
  setPlaybookValues: (arg: Partial<PlaybookValues>) => void;
  setPlaybookEnabled: (enabled: boolean) => void;
}

const handleLegacyCustomPlaybooks = (data: PlaybookResponse, country: string) => {
  if (!data.playbookValues) {
    return;
  }

  if (!data.playbookValues.governingLaw.value) {
    data.playbookValues.governingLaw.value = [
      country,
    ];
  }
  if (!data.playbookValues.disputeResolution.value) {
    data.playbookValues.disputeResolution.value = [];
  }
};

const mergePlaybookValues = (current, newValues) =>
  mergeWith({}, current, newValues, (currentValue, newValue) => {
    if (isArray(currentValue)) {
      return newValue;
    }
  });

export const usePlaybookStore = create<PlaybookStore>((set, get) => ({
  playbookValues: null,
  playbookEnabled: false,
  initializeDefaultValues: (
    workspaceType: string,
    country: string,
    t: (translations: Record<string, string>) => string,
  ) => {
    const current = get().playbookValues;

    if (current) {
      return;
    }

    const type = workspaceType === 'procurement' ? workspaceType : 'sales';
    set({
      playbookValues: getDefaultPlaybookValues(type, country, t),
    });
  },
  initializeResponseData: (responseData: PlaybookResponse, country: string) => {
    handleLegacyCustomPlaybooks(responseData, country);
    set({
      playbookEnabled: responseData.enabled,
      playbookValues: responseData.playbookValues,
    });
  },
  resetPlaybookValues: () =>
    set({
      playbookValues: null,
      playbookEnabled: false,
    }),
  setPlaybookValues: (values) => {
    const current = get().playbookValues;
    const updatedValues = Object.freeze(mergePlaybookValues(current, values));

    set({
      playbookValues: updatedValues,
    });
  },
  setPlaybookEnabled: (enabled: boolean) =>
    set({
      playbookEnabled: enabled,
    }),
}));
